// "Datos del producto" en Editar producto, debajo de las fotos y plegado de entrada. El SKU va
// aparte, debajo del nombre (`CampoDeSku`); el Tipo y las Categorías, debajo del SKU
// (`CategoriasDelProducto.tsx`).
// (ADR-2026-10-21-editar-producto-muestra-sus-datos, ADR-2026-10-21-datos-del-producto-plegados).
// Controlado: el estado y el guardado son de `useDetallesDelProducto`, y se guarda con "Listo"
// junto con el nombre. VariantForm lo dibuja dos veces (panel de escritorio y hoja de mobile), por
// eso los ids salen de `useId`.
import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import {
  MAX_LARGO_DESCRIPCION,
  MAX_LARGO_SKU,
  type DetallesDelProducto,
  type MedidasEscritas,
} from "../lib/detallesDelProducto";
import { MAX_CAMPOS_FICHA, MAX_LARGO_VALOR } from "../lib/fichaDeProducto";
import { categoriaAdmiteMedidas } from "../pages/v2/medidasApi";
import type { UnidadMedida } from "../lib/medidas";

const campoBase =
  "w-full border border-black/[0.08] bg-white text-[length:var(--text-body)] text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none transition-shadow placeholder:text-black/35 focus:border-brand-primary focus:ring-4 focus:ring-[#EBEFFE]";
const campoDeUnaLinea = `${campoBase} h-[var(--btn-h)] rounded-full px-4`;
const campoDeTexto = `${campoBase} min-h-[96px] resize-y rounded-2xl px-4 py-3 leading-snug`;

/** Alto máximo del análisis de IA: hasta ahí crece con el texto; con más, scrollea por dentro. */
const ALTO_MAXIMO_ANALISIS = 480;

/** Un textarea que crece con su contenido hasta `ALTO_MAXIMO_ANALISIS`. */
const TextoQueCrece: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }
> = (props) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    // Oculto (la copia de mobile en escritorio, o al revés) mide 0: queda en el `min-h`.
    if (!el || el.offsetParent === null) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, ALTO_MAXIMO_ANALISIS)}px`;
  }, [props.value]);
  return (
    <textarea
      ref={ref}
      {...props}
      style={{ maxHeight: ALTO_MAXIMO_ANALISIS }}
      className={`${campoBase} min-h-[96px] resize-none overflow-y-auto rounded-2xl px-4 py-3 leading-snug`}
    />
  );
};

const Etiqueta: React.FC<{
  htmlFor?: string;
  children: React.ReactNode;
  ayuda?: string;
}> = ({ htmlFor, children, ayuda }) => (
  <div className="mb-2 px-0.5">
    <label
      htmlFor={htmlFor}
      className="block text-[13px] font-semibold text-[#16181D]"
    >
      {children}
    </label>
    {ayuda && (
      <p className="mt-0.5 text-[12.5px] leading-snug text-black/45">{ayuda}</p>
    )}
  </div>
);

const UNIDADES: UnidadMedida[] = ["cm", "mm", "m"];
const DIMENSIONES: Array<{
  clave: keyof Omit<MedidasEscritas, "unidad">;
  etiqueta: string;
}> = [
  { clave: "alto", etiqueta: "Alto" },
  { clave: "ancho", etiqueta: "Ancho" },
  { clave: "profundidad", etiqueta: "Profundidad" },
];

export const DetallesDelProductoForm: React.FC<{
  detalles: DetallesDelProducto | null;
  onChange: (d: DetallesDelProducto) => void;
  /** La descripción de la tienda sólo se muestra si el producto vino con una. */
  conDescripcionDeTienda: boolean;
}> = ({ detalles, onChange, conDescripcionDeTienda }) => {
  const id = useId();
  const [abierto, setAbierto] = useState(false);
  if (!detalles) return null;

  const cambiar = (parcial: Partial<DetallesDelProducto>) =>
    onChange({ ...detalles, ...parcial });
  const conMedidas =
    detalles.esCatalogo && categoriaAdmiteMedidas(detalles.categoria);
  const lineas = detalles.caracteristicas;
  const cambiarLinea = (i: number, valor: string) =>
    cambiar({ caracteristicas: lineas.map((l, j) => (j === i ? valor : l)) });

  return (
    <div>
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-controls={`${id}-datos`}
        className="flex w-full items-center justify-between rounded-full px-0.5 py-1 text-left"
      >
        <span className="text-[13px] font-semibold text-[#16181D]">
          Datos del producto
        </span>
        <ChevronDown
          aria-hidden
          className={`h-4 w-4 text-black/45 transition-transform duration-200 ${abierto ? "rotate-180" : ""}`}
        />
      </button>
      {abierto && (
        <div id={`${id}-datos`} className="mt-4 space-y-6">
          {conDescripcionDeTienda && (
            <div>
              <Etiqueta htmlFor={`${id}-desc`} ayuda="La que trajo tu tienda.">
                Descripción
              </Etiqueta>
              <textarea
                id={`${id}-desc`}
                value={detalles.descripcion}
                maxLength={MAX_LARGO_DESCRIPCION}
                onChange={(e) => cambiar({ descripcion: e.target.value })}
                className={campoDeTexto}
              />
            </div>
          )}

          <div>
            <Etiqueta>Características</Etiqueta>
            <div className="space-y-2">
              {lineas.map((linea, i) => (
                <div key={i} className="relative">
                  <input
                    type="text"
                    value={linea}
                    maxLength={MAX_LARGO_VALOR + 42}
                    onChange={(e) => cambiarLinea(i, e.target.value)}
                    placeholder="Material: roble"
                    aria-label={`Característica ${i + 1}`}
                    className={`${campoDeUnaLinea} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      cambiar({
                        caracteristicas: lineas.filter((_, j) => j !== i),
                      })
                    }
                    title="Quitar característica"
                    aria-label="Quitar característica"
                    className="absolute inset-y-0 right-1.5 my-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-black/40 transition-colors hover:bg-black/[0.04] hover:text-error"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {lineas.length < MAX_CAMPOS_FICHA && (
                <button
                  type="button"
                  onClick={() => cambiar({ caracteristicas: [...lineas, ""] })}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium text-brand-primary transition-colors hover:bg-[#EBEFFE]"
                >
                  <Plus className="h-4 w-4" />
                  Agregar característica
                </button>
              )}
            </div>
          </div>

          {conMedidas && (
            <div>
              <Etiqueta ayuda="Ayudan a que la IA respete el tamaño real del producto.">
                Medidas
              </Etiqueta>
              <div className="grid grid-cols-3 gap-2">
                {DIMENSIONES.map((d) => (
                  <label key={d.clave} className="block">
                    <span className="mb-1 block px-1 text-[12px] text-black/50">
                      {d.etiqueta}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={detalles.medidas[d.clave]}
                      onChange={(e) =>
                        cambiar({
                          medidas: {
                            ...detalles.medidas,
                            [d.clave]: e.target.value,
                          },
                        })
                      }
                      placeholder="—"
                      className={`${campoDeUnaLinea} px-3 text-center`}
                    />
                  </label>
                ))}
              </div>
              <div
                className="mt-2.5 flex items-center gap-1.5 px-0.5"
                role="radiogroup"
                aria-label="Unidad"
              >
                <span className="mr-1 text-[12.5px] text-black/50">Unidad</span>
                {UNIDADES.map((u) => {
                  const activa = detalles.medidas.unidad === u;
                  return (
                    <button
                      key={u}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      onClick={() =>
                        cambiar({ medidas: { ...detalles.medidas, unidad: u } })
                      }
                      className={`rounded-full px-3 py-1 text-[12.5px] font-medium transition-colors ${
                        activa
                          ? "bg-brand-primary text-white"
                          : "bg-black/[0.04] text-black/60 hover:bg-black/[0.07]"
                      }`}
                    >
                      {u}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Etiqueta htmlFor={`${id}-ia`}>Análisis de IA</Etiqueta>
            <TextoQueCrece
              id={`${id}-ia`}
              value={detalles.descripcionVisual}
              maxLength={MAX_LARGO_DESCRIPCION}
              onChange={(e) => cambiar({ descripcionVisual: e.target.value })}
              placeholder="Se completa sola cuando subís las fotos."
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * El SKU, debajo del nombre y fuera de "Datos del producto": es el código con el que la persona
 * identifica el producto, no un dato para la IA (ADR-2026-10-21-el-sku-es-un-campo-propio). Va en
 * `variantes.sku`, la columna que las importaciones usan para no duplicar productos.
 */
export const CampoDeSku: React.FC<{
  detalles: DetallesDelProducto | null;
  onChange: (d: DetallesDelProducto) => void;
}> = ({ detalles, onChange }) => {
  const id = useId();
  if (!detalles) return null;
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <label
          htmlFor={`${id}-sku`}
          className="text-[13px] font-semibold text-[#16181D]"
        >
          SKU
        </label>
        <span className="text-[12.5px] text-black/40">Opcional</span>
      </div>
      <input
        id={`${id}-sku`}
        type="text"
        value={detalles.sku}
        maxLength={MAX_LARGO_SKU}
        onChange={(e) => onChange({ ...detalles, sku: e.target.value })}
        placeholder="El código con el que lo identificás"
        autoComplete="off"
        spellCheck={false}
        className={campoDeUnaLinea}
      />
    </div>
  );
};
